import React, { useState, useMemo } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { getVisibleProgressColleges } from '../helpers/progressSource';
import './CalendarPage.css';
import { userStorage } from '../lib/userStorage';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Read all calendar events: tasks with dates + college deadlines.
 * Returns a Map of dateString → [{ type, label, college, ... }]
 */
export function buildCalendarEvents(colleges) {
  const events = new Map(); // 'YYYY-MM-DD' → [event, ...]

  const addEvent = (dateStr, event) => {
    if (!dateStr) return;
    if (!events.has(dateStr)) events.set(dateStr, []);
    events.get(dateStr).push(event);
  };

  try {
    const generalCards = JSON.parse(userStorage.getItem('progress_general_cards'));
    if (Array.isArray(generalCards)) {
      generalCards.forEach((card) => {
        if (!Array.isArray(card.items)) return;
        card.items.forEach((item) => {
          if (!item.date) return;
          addEvent(item.date, {
            type: 'task',
            label: item.text,
            college: `General Progress · ${card.title}`,
            status: item.complete ? 'complete' : 'todo',
            priority: '',
          });
        });
      });
    }
  } catch { /* skip */ }

  colleges.forEach((college) => {
    const safeKey = college.name.replace(/\s+/g, '_');

    // Read tasks
    try {
      const raw = userStorage.getItem(`progress_tasks_${safeKey}`);
      if (raw) {
        const tasks = JSON.parse(raw);
        if (Array.isArray(tasks)) {
          tasks.forEach((t) => {
            if (t.date) {
              addEvent(t.date, {
                type: 'task',
                label: t.text,
                college: college.name,
                status: t.status || 'todo',
                priority: t.priority || '',
              });
            }
          });
        }
      }
    } catch { /* skip */ }

    // Read deadlines
    try {
      const raw = userStorage.getItem(`progress_meta_${safeKey}`);
      if (raw) {
        const meta = JSON.parse(raw);
        if (meta.applyByDate) {
          addEvent(meta.applyByDate, {
            type: 'deadline',
            label: meta.applicationType || 'Application',
            college: college.name,
            status: meta.applicationSubmitted ? 'complete' : 'todo',
          });
        }
      }
    } catch { /* skip */ }
  });

  return events;
}

/**
 * Generate an array of { date, isCurrentMonth } for the calendar grid.
 */
function getCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDow = firstDay.getDay(); // 0 = Sun
  const daysInMonth = lastDay.getDate();

  const days = [];

  // Padding days from previous month
  const prevLast = new Date(year, month, 0).getDate();
  for (let i = startDow - 1; i >= 0; i--) {
    const d = new Date(year, month - 1, prevLast - i);
    days.push({ date: d, isCurrentMonth: false });
  }

  // Current month days
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({ date: new Date(year, month, d), isCurrentMonth: true });
  }

  // Padding days from next month (fill to 6 rows = 42 cells, or 5 rows = 35)
  const totalCells = days.length <= 35 ? 35 : 42;
  let nextDay = 1;
  while (days.length < totalCells) {
    days.push({ date: new Date(year, month + 1, nextDay++), isCurrentMonth: false });
  }

  return days;
}

function formatDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/* ─── Day Cell ─── */
function DayCell({ day, events, isToday, selectedDate, onSelect }) {
  const dateKey = formatDateKey(day.date);
  const dayEvents = events.get(dateKey) || [];
  const deadlines = dayEvents.filter((e) => e.type === 'deadline');
  const activeDeadlines = deadlines.filter((deadline) => deadline.status !== 'complete');
  const tasks = dayEvents.filter((e) => e.type === 'task');
  const isSelected = selectedDate === dateKey;

  return (
    <button
      className={`cal-day ${!day.isCurrentMonth ? 'cal-day--outside' : ''} ${isToday ? 'cal-day--today' : ''} ${isSelected ? 'cal-day--selected' : ''} ${activeDeadlines.length > 0 ? 'cal-day--has-deadline' : ''}`}
      onClick={() => onSelect(isSelected ? null : dateKey)}
    >
      <span className="cal-day__number">{day.date.getDate()}</span>
      {dayEvents.length > 0 && (
        <div className="cal-day__indicators">
          {deadlines.map((d, i) => (
            <span
              key={`dl-${i}`}
              className={`cal-day__dot cal-day__dot--deadline ${d.status === 'complete' ? 'cal-day__dot--complete' : ''}`}
              title={`${d.college}: ${d.label}${d.status === 'complete' ? ' (submitted)' : ''}`}
            />
          ))}
          {tasks.slice(0, 3).map((t, i) => (
            <span
              key={`t-${i}`}
              className={`cal-day__dot cal-day__dot--task ${t.status === 'complete' ? 'cal-day__dot--complete' : ''} ${t.status === 'in-progress' ? 'cal-day__dot--in-progress' : ''}`}
              title={`${t.college}: ${t.label}`}
            />
          ))}
          {tasks.length > 3 && (
            <span className="cal-day__dot cal-day__dot--more">+{tasks.length - 3}</span>
          )}
        </div>
      )}
    </button>
  );
}

/* ─── Detail Panel for selected day ─── */
function DayDetail({ dateKey, events }) {
  if (!dateKey) return null;
  const dayEvents = events.get(dateKey) || [];
  if (dayEvents.length === 0) {
    const d = new Date(dateKey + 'T00:00:00');
    const formatted = d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    return (
      <div className="cal-detail">
        <h3 className="cal-detail__date">{formatted}</h3>
        <p className="cal-detail__empty">No events on this day</p>
      </div>
    );
  }

  const d = new Date(dateKey + 'T00:00:00');
  const formatted = d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const deadlines = dayEvents.filter((e) => e.type === 'deadline');
  const tasks = dayEvents.filter((e) => e.type === 'task');

  return (
    <div className="cal-detail">
      <h3 className="cal-detail__date">{formatted}</h3>

      {deadlines.length > 0 && (
        <div className="cal-detail__section">
          <span className="cal-detail__section-title cal-detail__section-title--deadline">
            Deadlines
          </span>
          {deadlines.map((dl, i) => (
            <div key={i} className={`cal-detail__item cal-detail__item--deadline ${dl.status === 'complete' ? 'cal-detail__item--complete cal-detail__item--deadline-complete' : ''}`}>
              <div className="cal-detail__deadline-heading">
                <span className="cal-detail__item-college">{dl.college}</span>
                {dl.status === 'complete' && (
                  <span className="cal-detail__completed-badge">
                    <CheckCircle2 size={13} strokeWidth={2.2} />
                    Submitted
                  </span>
                )}
              </div>
              <span className="cal-detail__item-label">{dl.label}</span>
            </div>
          ))}
        </div>
      )}

      {tasks.length > 0 && (
        <div className="cal-detail__section">
          <span className="cal-detail__section-title cal-detail__section-title--task">
            Tasks
          </span>
          {tasks.map((t, i) => (
            <div key={i} className={`cal-detail__item cal-detail__item--task ${t.status === 'complete' ? 'cal-detail__item--complete' : ''}`}>
              {t.status === 'complete' ? (
                <CheckCircle2 className="cal-detail__complete-icon" size={15} strokeWidth={2.2} />
              ) : (
                <span className={`cal-detail__status-dot cal-detail__status-dot--${t.status}`} />
              )}
              <div className="cal-detail__item-body">
                <span className="cal-detail__item-label">{t.label}</span>
                <span className="cal-detail__item-college">{t.college}</span>
              </div>
              {t.priority && (
                <span className={`cal-detail__priority cal-detail__priority--${t.priority.toLowerCase()}`}>
                  {t.priority}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Main Calendar Page ─── */
function CalendarPage() {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);

  const colleges = useMemo(() => getVisibleProgressColleges(), []);
  const events = useMemo(() => buildCalendarEvents(colleges), [colleges]);
  const calendarDays = useMemo(() => getCalendarDays(viewYear, viewMonth), [viewYear, viewMonth]);

  const todayKey = formatDateKey(today);

  const goPrev = () => {
    if (viewMonth === 0) {
      setViewYear((y) => y - 1);
      setViewMonth(11);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const goNext = () => {
    if (viewMonth === 11) {
      setViewYear((y) => y + 1);
      setViewMonth(0);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const goToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
    setSelectedDate(todayKey);
  };

  // Count total events this month
  const monthEventCount = calendarDays
    .filter((d) => d.isCurrentMonth)
    .reduce((sum, d) => sum + (events.get(formatDateKey(d.date)) || []).length, 0);

  const monthDeadlineCount = calendarDays
    .filter((d) => d.isCurrentMonth)
    .reduce((sum, d) => {
      const evts = events.get(formatDateKey(d.date)) || [];
      return sum + evts.filter((e) => e.type === 'deadline').length;
    }, 0);

  return (
    <div className="calendar-page">
      <div className="calendar-page__header">
        <div>
          <h1 className="calendar-page__title">Calendar</h1>
          <p className="calendar-page__subtitle">
            Calendar shows recommended General Progress dates plus deadlines and tasks from your final colleges.
          </p>
        </div>
        <div className="calendar-page__stats">
          {monthDeadlineCount > 0 && (
            <span className="calendar-page__stat calendar-page__stat--deadline">
              <span className="calendar-page__stat-dot calendar-page__stat-dot--deadline" />
              {monthDeadlineCount} {monthDeadlineCount === 1 ? 'deadline' : 'deadlines'}
            </span>
          )}
          {monthEventCount > 0 && (
            <span className="calendar-page__stat calendar-page__stat--task">
              <span className="calendar-page__stat-dot calendar-page__stat-dot--task" />
              {monthEventCount} {monthEventCount === 1 ? 'event' : 'events'}
            </span>
          )}
        </div>
      </div>

      <div className="calendar-page__nav">
        <button className="calendar-page__nav-btn" onClick={goPrev} aria-label="Previous month">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="calendar-page__nav-center">
          <span className="calendar-page__month-label">
            {MONTH_NAMES[viewMonth]} {viewYear}
          </span>
          <button className="calendar-page__today-btn" onClick={goToday} type="button">
            Today
          </button>
        </div>
        <button className="calendar-page__nav-btn" onClick={goNext} aria-label="Next month">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <div className="calendar-page__body">
        <div className="calendar-page__grid-wrapper">
          {/* Day-of-week headers */}
          <div className="cal-header">
            {DAY_LABELS.map((d) => (
              <span key={d} className="cal-header__label">{d}</span>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="cal-grid">
            {calendarDays.map((day, i) => (
              <DayCell
                key={i}
                day={day}
                events={events}
                isToday={formatDateKey(day.date) === todayKey}
                selectedDate={selectedDate}
                onSelect={setSelectedDate}
              />
            ))}
          </div>

          {/* Legend */}
          <div className="cal-legend">
            <div className="cal-legend__item">
              <span className="cal-legend__dot cal-legend__dot--deadline" />
              <span className="cal-legend__text">Deadline</span>
            </div>
            <div className="cal-legend__item">
              <span className="cal-legend__dot cal-legend__dot--task" />
              <span className="cal-legend__text">Task (To Do)</span>
            </div>
            <div className="cal-legend__item">
              <span className="cal-legend__dot cal-legend__dot--in-progress" />
              <span className="cal-legend__text">In Progress</span>
            </div>
            <div className="cal-legend__item">
              <span className="cal-legend__dot cal-legend__dot--complete" />
              <span className="cal-legend__text">Complete</span>
            </div>
          </div>
        </div>

        {/* Detail panel */}
        <DayDetail dateKey={selectedDate} events={events} />
      </div>
    </div>
  );
}

export default CalendarPage;
